import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { RequestIndicator, ThemeToggle, ToastViewport, useAuth } from 'grabbitbot-legacy-ui'
import { apiClient } from '../api/client'
import { ADMIN_ROLE, DEFAULT_EXCHANGE_ID } from '../config'
import { utcDateText } from '../pages/payment/planStatus'
import { usePortalHeader } from './usePortalHeader'

interface NavEntry {
  label: string
  /** Link target; may carry a query string (AutoBuy report). */
  to: string
  /** Extra path prefixes that also count as "this item" (nested routes). */
  alsoActive?: string[]
  /** Active on any path under the statistics prefix ending with this suffix
   * (the coin treasury page carries the coin in its path). */
  activeSuffix?: string
}

interface NavGroup {
  /** Small uppercase heading; `null` for the ungrouped Dashboard entry. */
  heading: string | null
  items: NavEntry[]
}

function pathMatches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`)
}

function isActive(entry: NavEntry, pathname: string, search: string): boolean {
  const [path = '', query = ''] = entry.to.split('?')
  if (pathMatches(pathname, path)) {
    // Two entries share the transactions path and differ only by query —
    // the query decides which one lights up.
    if (query) return search === `?${query}`
    if (path.endsWith('/statistics/transactions')) return !search.includes('tag=source_autobuy')
    return true
  }
  if (entry.activeSuffix && pathname.endsWith(entry.activeSuffix)) {
    const prefix = entry.to.slice(0, entry.to.lastIndexOf('/', entry.to.lastIndexOf('/') - 1))
    if (pathname.startsWith(`${prefix}/`)) return true
  }
  return (entry.alsoActive ?? []).some((p) => pathMatches(pathname, p))
}

function buildGroups(ex: string, tt: string, isAdmin: boolean): NavGroup[] {
  const stats = `/${ex}/${tt}/statistics`
  const groups: NavGroup[] = [
    { heading: null, items: [{ label: 'Dashboard', to: '/dashboard', alsoActive: [`/${ex}/${tt}/dashboard`] }] },
    {
      heading: 'Trading',
      items: [
        { label: 'Coins', to: `/${ex}/${tt}/coins` },
        { label: 'Strategies', to: `/strategy/${tt}` },
        { label: 'Default coin config', to: '/settings/default' },
      ],
    },
    {
      heading: 'Statistics',
      items: [
        { label: 'Open positions', to: `${stats}/positions/open` },
        { label: 'Closed positions', to: `${stats}/positions/closed` },
        // The coin-summary route stays (placeholder page); it belongs to the
        // same menu entry as the profit summary.
        { label: 'Profit & coin summary', to: `${stats}/global/profit-summary`, alsoActive: [`${stats}/global/coin-summary`] },
        { label: 'Coin transactions', to: `${stats}/transactions` },
        { label: 'AutoBuy transactions', to: `${stats}/transactions?tag=source_autobuy` },
        { label: 'Stuck coins', to: `${stats}/stuck-coins` },
        // Treasury: the coin balance at each hourly / daily close. Opens on
        // USDC; the page itself switches coin (any `${stats}/<coin>/treasury`).
        { label: 'Coin treasury', to: `${stats}/USDC/treasury`, activeSuffix: '/treasury' },
      ],
    },
    {
      heading: 'Account',
      items: [
        { label: 'Exchange keys', to: '/settings/exchange' },
        { label: 'Payment & credit', to: '/payment' },
        // /settings/service-accounts is the canonical route (SettingsTabs);
        // /service-accounts is the legacy alias.
        { label: 'Service accounts', to: '/settings/service-accounts', alsoActive: ['/service-accounts'] },
        { label: 'Profile', to: '/user' },
      ],
    },
  ]
  if (isAdmin) {
    groups.push({
      heading: 'Admin',
      items: [
        { label: 'Users & bots', to: '/admin/users' },
        { label: 'Payments & logs', to: '/admin/payment' },
      ],
    })
  }
  return groups
}

/**
 * Grouped left sidebar (owner decision 2026-10-12, replacing the 11-link top
 * menu that overflowed): GrabbitBot title, exchange picker, then the nav
 * groups. Below ~900px the sidebar becomes a drawer behind a "Menu" button.
 * See src/routes/AppRoutes.tsx for the routes each entry points at.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const isAdmin = Boolean(user?.roles?.includes(ADMIN_ROLE))
  const { exchangeId: exchangeIdParam, tradingType: tradingTypeParam } = useParams<{
    exchangeId?: string
    tradingType?: string
  }>()
  const { tradingType, exchanges, selectedExchangeId, paymentRequired, canceledUntil } = usePortalHeader(
    user,
    exchangeIdParam,
    tradingTypeParam,
  )
  // Falls back to the config-only placeholder scope until an exchange
  // account is actually configured (`DEFAULT_EXCHANGE_ID`, see src/config.ts).
  const statsExchangeId = selectedExchangeId || DEFAULT_EXCHANGE_ID
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  if (!user) {
    return (
      <div className="portal-layout portal-layout--bare">
        <main className="portal-main">{children}</main>
        <ToastViewport />
      </div>
    )
  }

  const groups = buildGroups(statsExchangeId, tradingType, isAdmin)
  let current: { group: string | null; label: string } | null = null
  for (const group of groups) {
    for (const item of group.items) {
      if (!current && isActive(item, location.pathname, location.search)) current = { group: group.heading, label: item.label }
    }
  }
  const profileActive = location.pathname === '/user'

  return (
    <div className="portal-layout">
      <aside className={menuOpen ? 'portal-sidebar portal-sidebar--open' : 'portal-sidebar'}>
        <div className="portal-sidebar__top">
          <Link to="/dashboard" className="portal-sidebar__title" onClick={() => setMenuOpen(false)}>
            GrabbitBot
          </Link>
          {/* Legacy `V3HeaderComponent` exchange picker: trading type is a
              single fixed option in round 1 (marginbot is not rewritten), so
              it renders as a static label next to the exchange <select>. */}
          <div className="portal-sidebar__account">
            <span className="portal-trading-type" aria-label="Trading type">
              {tradingType.toUpperCase()}
            </span>
            <label className="portal-exchange-picker">
              <span className="sr-only">Exchange account</span>
              <select
                aria-label="Exchange account"
                value={selectedExchangeId}
                disabled={!exchanges || exchanges.length === 0}
                onChange={(event) => {
                  const value = event.target.value
                  if (value) {
                    localStorage.setItem('lastExchangeId', value)
                    localStorage.setItem('lastTradingType', tradingType)
                    window.location.assign(`/${value}/${tradingType}/dashboard`)
                  }
                }}
              >
                {!exchanges && <option value="">loading exchange list …</option>}
                {exchanges && exchanges.length === 0 && <option value="">no exchanges configured yet</option>}
                {exchanges?.map((option) => (
                  <option key={option.exchangeId} value={option.exchangeId}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <nav id="portal-sidebar-nav" className="portal-sidebar__nav" aria-label="Main" onClick={(e) => {
          if ((e.target as HTMLElement).closest('a')) setMenuOpen(false)
        }}>
          {groups.map((group) => (
            <div key={group.heading ?? 'top'} className="portal-sidebar__group">
              {group.heading && <p className="portal-sidebar__heading">{group.heading}</p>}
              <ul>
                {group.items.map((item) => {
                  const active = isActive(item, location.pathname, location.search)
                  return (
                    <li key={item.to}>
                      <Link to={item.to} aria-current={active ? 'page' : undefined} className={active ? 'active' : undefined}>
                        {item.label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      {menuOpen && <div className="portal-sidebar-backdrop" aria-hidden="true" onClick={() => setMenuOpen(false)} />}
      <div className="portal-content">
        <header className="portal-topbar">
          <button
            ref={menuButtonRef}
            type="button"
            className="portal-menu-button"
            aria-expanded={menuOpen}
            aria-controls="portal-sidebar-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            Menu
          </button>
          <p className="portal-breadcrumb">
            <span>GrabbitBot</span>
            {current ? (
              <>
                {current.group && <span> / {current.group}</span>}
                <span> / </span>
                <strong>{current.label}</strong>
              </>
            ) : null}
          </p>
          <div className="portal-actions gbx-header__actions">
            <RequestIndicator client={apiClient} />
            <ThemeToggle />
            <Link to="/user" aria-current={profileActive ? 'page' : undefined} className="portal-username">
              {user.username}
            </Link>
            <button type="button" onClick={logout}>
              Log out
            </button>
          </div>
        </header>
        {paymentRequired && (
          <p role="alert" className="portal-banner portal-banner--danger">
            Please start your subscription <Link to="/payment">here</Link>, so you can use GrabbitBot!
          </p>
        )}
        {canceledUntil && (
          <p role="status" className="portal-banner portal-banner--info">
            Canceled, active until <time dateTime={canceledUntil}>{utcDateText(canceledUntil)}</time> — <Link to="/payment">manage your subscription</Link>
          </p>
        )}
        <main className="portal-main">{children}</main>
      </div>
      <ToastViewport />
    </div>
  )
}
